/**
 * React Query configuration and query keys.
 *
 * Provides:
 * - Global QueryClient with sensible defaults
 * - Structured query keys for cache management
 * - Default error handling for API errors
 */

import {
  MutationCache,
  QueryClient,
  QueryClientProvider as TanStackQueryClientProvider,
} from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import { stockQueryKeys } from '@/api/stock/queryKeys';

const DEFAULT_QUERY_RETRY_COUNT = 1;

/**
 * Structured query keys for consistent cache management.
 *
 * Convention:
 * - `.all` returns the base key for invalidating all related queries
 * - `.list(filters)` returns key for filtered lists
 * - `.detail(id)` returns key for single item
 *
 * @example
 * // Invalidate all invoices
 * queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
 *
 * // Invalidate specific invoice
 * queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(123) })
 */
export const queryKeys = {
  invoices: {
    all: ['invoices'] as const,
    /** Prefix for all list queries - use with resetQueries to clear all paginated lists */
    listAll: ['invoices', 'list'] as const,
    list: (filters: object) => ['invoices', 'list', filters] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    ocrDebug: (id: string) => ['invoices', 'ocr-debug', id] as const,
    ksefPdfAnchors: (id: string, invoiceSignature: string, pdfUrl: string) =>
      ['invoices', 'ksef-pdf-anchors', id, invoiceSignature, pdfUrl] as const,
    duplicateCheck: (id: string) => ['invoices', 'duplicate-check', id] as const,
    counts: (filters?: object) =>
      filters ? (['invoices', 'counts', filters] as const) : (['invoices', 'counts'] as const),
  },
  payments: {
    all: ['payments'] as const,
    duplicateHints: (documentIds: string[]) =>
      ['payments', 'duplicate-hints', ...documentIds] as const,
    sends: ['payments', 'sends'] as const,
    declared: ['payments', 'declared'] as const,
    paidChecks: (documentIds: string[]) => ['payments', 'paid-checks', ...documentIds] as const,
    salary: ['payments', 'salary'] as const,
    ibanChecks: (items: string[]) => ['payments', 'iban-checks', ...items] as const,
    whitelist: (documentIds: string[], iban: string) =>
      ['payments', 'whitelist', iban, ...documentIds] as const,
  },
  documentLinks: {
    all: ['documentLinks'] as const,
    byDocument: (documentId: string) => ['documentLinks', documentId] as const,
    suggestions: (documentId: string, linkType: string, limit: number) =>
      ['documentLinks', 'suggestions', documentId, linkType, limit] as const,
    relationsSummary: (ids: string[]) => ['documentLinks', 'relationsSummary', ...ids] as const,
  },
  lookups: {
    byType: (type: string) => ['lookups', type] as const,
  },
  entities: {
    list: (filters: object) => ['entities', 'list', filters] as const,
  },
  supplierBankAccounts: {
    all: ['supplierBankAccounts'] as const,
    bySupplier: (supplierId: string) => ['supplierBankAccounts', supplierId] as const,
  },
  buyerCompanyBankAccounts: {
    all: ['buyerCompanyBankAccounts'] as const,
    byBuyerCompany: (buyerCompanyId: string) =>
      ['buyerCompanyBankAccounts', buyerCompanyId] as const,
  },
  finance: {
    all: ['finance'] as const,
    receipts: (filters: object) => ['finance', 'receipts', filters] as const,
    receipt: (id: string) => ['finance', 'receipt', id] as const,
    inbox: () => ['finance', 'inbox'] as const,
    cardTerminals: () => ['finance', 'card-terminals'] as const,
    receivables: (sellerCompanyKey: string | null) =>
      ['finance', 'receivables', sellerCompanyKey ?? 'all'] as const,
  },
  reconciliation: {
    all: ['reconciliation'] as const,
    cases: (filters: object) => ['reconciliation', 'cases', filters] as const,
    case: (caseKey: string) => ['reconciliation', 'case', caseKey] as const,
    decisions: (filters: object) => ['reconciliation', 'decisions', filters] as const,
  },
  enableBanking: {
    all: ['enableBanking'] as const,
    status: () => ['enableBanking', 'status'] as const,
    accounts: (filters?: { date_from?: string; date_to?: string }) =>
      filters
        ? (['enableBanking', 'accounts', filters] as const)
        : (['enableBanking', 'accounts'] as const),
    transactions: (
      accountUid: string,
      filters: { date_from?: string; date_to?: string; search?: string },
    ) => ['enableBanking', 'transactions', accountUid, filters] as const,
    transactionRaw: (
      accountUid: string,
      filters: {
        transaction_id?: string | null;
        entry_reference?: string | null;
        date_from?: string;
        date_to?: string;
      },
    ) => ['enableBanking', 'transactionRaw', accountUid, filters] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    activityOverview: (params: object) => ['analytics', 'activity', 'overview', params] as const,
    rawPurchasesReportAll: ['analytics', 'raw-purchases'] as const,
    rawPurchasesReport: (params: object) => ['analytics', 'raw-purchases', params] as const,
    platformReport: (params: object) => ['analytics', 'platforms', params] as const,
    dashboardOverview: (filters: object) =>
      ['analytics', 'dashboard', 'overview', filters] as const,
    revenueTrend: (filters: object) =>
      ['analytics', 'dashboard', 'revenue-trend', filters] as const,
    salesOverview: (filters: object) => ['analytics', 'sales', 'overview', filters] as const,
    salesOverviewCategories: (filters: object) =>
      ['analytics', 'sales', 'overview-categories', filters] as const,
    salesOverviewSummary: (filters: object) =>
      ['analytics', 'sales', 'overview-summary', filters] as const,
    salesProductCategories: (filters: object) =>
      ['analytics', 'sales', 'product-categories', filters] as const,
    salesProductGroups: (filters: object) =>
      ['analytics', 'sales', 'product-groups', filters] as const,
    salesProductCategoryDetail: (filters: object) =>
      ['analytics', 'sales', 'product-category-detail', filters] as const,
    salesProductsList: (filters: object) =>
      ['analytics', 'sales', 'products-list', filters] as const,
    salesProducts: (filters: object) => ['analytics', 'sales', 'products', filters] as const,
    salesLocations: (filters: object) => ['analytics', 'sales', 'locations', filters] as const,
    salesTimePatterns: (filters: object) =>
      ['analytics', 'sales', 'time-patterns', filters] as const,
    salesTimeSlotDetail: (filters: object) =>
      ['analytics', 'sales', 'time-slot-detail', filters] as const,
    pnlOverview: (filters: object) => ['analytics', 'pnl', 'overview', filters] as const,
    pnlVatEstimate: (filters: object) => ['analytics', 'pnl', 'vat-estimate', filters] as const,
    pnlPayrollFacts: (filters: object) => ['analytics', 'pnl', 'payroll-facts', filters] as const,
    pnlBankPayments: (filters: object, kind: string) =>
      ['analytics', 'pnl', 'bank-payments', kind, filters] as const,
    locationExpenses: (locationId: string, filters: object) =>
      ['analytics', 'pnl', 'location-expenses', locationId, filters] as const,
    cogsAudit: (filters: object) => ['analytics', 'pnl', 'cogs-audit', filters] as const,
    pnlConstructorTemplate: () => ['analytics', 'pnl', 'constructor', 'template'] as const,
    pnlConstructorEntries: (yearMonth: string) =>
      ['analytics', 'pnl', 'constructor', 'entries', yearMonth] as const,
    salesLocationDetail: (filters: object) =>
      ['analytics', 'sales', 'location-detail', filters] as const,
    salesB2B: (filters: object) => ['analytics', 'sales', 'b2b', filters] as const,
    salesB2BDetail: (filters: object) => ['analytics', 'sales', 'b2b-detail', filters] as const,
  },
  documentWarnings: {
    all: ['documentWarnings'] as const,
    list: (filters: object) => ['documentWarnings', 'list', filters] as const,
    deliveryMismatch: () => ['documentWarnings', 'delivery-mismatch'] as const,
  },
  processingJobs: {
    all: ['processingJobs'] as const,
    list: () => ['processingJobs', 'list'] as const,
  },
  references: {
    all: ['references'] as const,
    type: (type: string) => ['references', type] as const,
    list: (type: string, filters: object) => ['references', type, 'list', filters] as const,
    detail: (type: string, id: string) => ['references', type, 'detail', id] as const,
  },
  nipLookup: {
    all: ['nipLookup'] as const,
    supplier: (nip: string, name?: string | null) =>
      ['nipLookup', 'supplier', nip, (name ?? '').trim().toLowerCase()] as const,
    buyer: (nip: string, name?: string | null) =>
      ['nipLookup', 'buyer', nip, (name ?? '').trim().toLowerCase()] as const,
  },
  users: {
    all: ['users'] as const,
    list: () => ['users', 'list'] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  pos: {
    all: ['pos'] as const,
    settings: (storeId: string) => ['pos', 'settings', storeId] as const,
    continuity: () => ['pos', 'continuity'] as const,
    discounts: () => ['pos', 'discounts'] as const,
    discount: (discountId: string) => ['pos', 'discount', discountId] as const,
    employees: () => ['pos', 'employees'] as const,
    employeeLinkCandidates: () => ['pos', 'employees', 'link-candidates'] as const,
    cashReasons: () => ['pos', 'cash-reasons'] as const,
    supportSessions: (limit = 50) => ['pos', 'support-sessions', limit] as const,
    supportSessionDetail: (sessionId: string) =>
      ['pos', 'support-session-detail', sessionId] as const,
    devicesStatus: () => ['pos', 'devices', 'status'] as const,
    devicesHealth: () => ['pos', 'devices', 'health'] as const,
    deviceCommands: (deviceId: string) => ['pos', 'devices', 'commands', deviceId] as const,
    deviceArtifactImage: (artifactId: string) =>
      ['pos', 'devices', 'artifact-image', artifactId] as const,
    terminalDeviceConfig: (terminalId: string) =>
      ['pos', 'terminal-device-config', terminalId] as const,
    appReleases: () => ['pos', 'app-releases'] as const,
    employeeShifts: (employeeId: string, limit: number, offset: number) =>
      ['pos', 'employee-shifts', employeeId, limit, offset] as const,
  },
  gopos: {
    all: ['gopos'] as const,
    orders: (filters: object) => ['gopos', 'orders', filters] as const,
    orderDays: (filters: object) => ['gopos', 'orders', 'days', filters] as const,
    ordersForDay: (day: string, filters: object) =>
      ['gopos', 'orders', 'day', day, filters] as const,
    orderChanges: (filters: object) => ['gopos', 'orders', 'changes', filters] as const,
    order: (id: string) => ['gopos', 'orders', 'detail', id] as const,
    venues: (options?: { include_virtual_pos_locations?: boolean }) =>
      ['gopos', 'venues', Boolean(options?.include_virtual_pos_locations)] as const,
    productsAll: ['gopos', 'products'] as const,
    products: (filters: object) => ['gopos', 'products', filters] as const,
    productsLookup: (ids: string[]) => ['gopos', 'products', 'lookup', ids] as const,
    product: (id: string) => ['gopos', 'products', 'detail', id] as const,
    categories: () => ['gopos', 'categories'] as const,
    internalCategories: {
      all: ['gopos', 'internal-categories'] as const,
      list: () => ['gopos', 'internal-categories', 'list'] as const,
    },
    supplyCategories: {
      all: ['gopos', 'supply-categories'] as const,
      list: () => ['gopos', 'supply-categories', 'list'] as const,
    },
    issuesSummary: () => ['gopos', 'issues-summary'] as const,
    syncStatus: () => ['gopos', 'sync-status'] as const,
    syncRunsAll: ['gopos', 'sync-runs'] as const,
    syncRuns: (filters: object) => ['gopos', 'sync-runs', filters] as const,
    syncBatchesAll: ['gopos', 'sync-batches'] as const,
    syncBatches: (filters: object) => ['gopos', 'sync-batches', filters] as const,
    syncStatsAll: ['gopos', 'sync-stats'] as const,
    syncStats: (periodHours: number) => ['gopos', 'sync-stats', periodHours] as const,
    jobs: () => ['gopos', 'jobs'] as const,
    job: (id: string) => ['gopos', 'jobs', 'detail', id] as const,
  },
  products: {
    all: ['products'] as const,
    list: (filters: object) => ['products', 'list', filters] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    prices: (id: string, grouped: boolean) => ['products', 'prices', id, grouped] as const,
    barcodes: (id: string) => ['products', 'barcodes', id] as const,
    sales7d: (id: string) => ['products', 'sales-7d', id] as const,
    purchaseOverview: (id: string, days: number) =>
      ['products', 'purchase-overview', id, days] as const,
    purchaseSummary: (id: string, days: number, asOfDate?: string) =>
      ['products', 'purchase-summary', id, days, asOfDate ?? null] as const,
    purchaseHistory: (id: string, filters: object) =>
      ['products', 'purchase-history', id, filters] as const,
    recipeUsage: (id: string) => ['products', 'recipe-usage', id] as const,
    syncStatus: (id: string) => ['products', 'sync-status', id] as const,
    auditLog: (id: string, limit: number, offset: number) =>
      ['products', 'audit-log', id, limit, offset] as const,
    categories: () => ['products', 'categories'] as const,
    issuesSummary: (categoryId?: string) =>
      categoryId
        ? (['products', 'issues-summary', categoryId] as const)
        : (['products', 'issues-summary'] as const),
  },
  geminiHealth: {
    all: ['geminiHealth'] as const,
    status: () => [...queryKeys.geminiHealth.all, 'status'] as const,
    history: (filters: object) => [...queryKeys.geminiHealth.all, 'history', filters] as const,
    detail: (id: string) => [...queryKeys.geminiHealth.all, 'detail', id] as const,
    ocrUsageSummary: (environment?: string) =>
      [...queryKeys.geminiHealth.all, 'ocr-usage', 'summary', environment ?? 'all'] as const,
    ocrUsageHistory: (filters: object) =>
      [...queryKeys.geminiHealth.all, 'ocr-usage', 'history', filters] as const,
    ocrUsageDetail: (id: string) =>
      [...queryKeys.geminiHealth.all, 'ocr-usage', 'detail', id] as const,
    ocrUsageCompare: (leftId: string, rightId: string) =>
      [...queryKeys.geminiHealth.all, 'ocr-usage', 'compare', leftId, rightId] as const,
  },
  ksef: {
    all: ['ksef'] as const,
    status: (company?: string | null) =>
      [...queryKeys.ksef.all, 'status', company ?? 'all'] as const,
    invoices: (filters: object) => [...queryKeys.ksef.all, 'invoices', filters] as const,
    invoice: (id: string) => [...queryKeys.ksef.all, 'invoice', id] as const,
    invoiceXml: (id: string) => [...queryKeys.ksef.all, 'invoiceXml', id] as const,
  },
  stock: stockQueryKeys,
  shelfLabels: {
    storeAll: ['shelf-labels', 'store'] as const,
    store: (storeId: string, filters: object) => ['shelf-labels', 'store', storeId, filters] as const,
    layout: (storeId: string, picked: object) =>
      ['shelf-labels', 'layout', storeId, picked] as const,
    product: (productId: string) => ['shelf-labels', 'product', productId] as const,
  },
  stores: {
    all: ['stores'] as const,
    list: (filters: object) => ['stores', 'list', filters] as const,
    detail: (id: string) => ['stores', 'detail', id] as const,
    overview: {
      all: ['stores', 'overview'] as const,
      single: (storeId: string, date?: string) =>
        ['stores', 'overview', 'single', storeId, date ?? 'today'] as const,
      allStores: (date?: string) => ['stores', 'overview', 'all-stores', date ?? 'today'] as const,
    },
    cash: {
      all: ['stores', 'cash'] as const,
      snapshot: (storeId: string, filters: object) =>
        ['stores', 'cash', 'snapshot', storeId, filters] as const,
      operations: (storeId: string, filters: object) =>
        ['stores', 'cash', 'operations', storeId, filters] as const,
      balance: (storeId: string) => ['stores', 'cash', 'balance', storeId] as const,
      summary: (storeId: string, filters: object) =>
        ['stores', 'cash', 'summary', storeId, filters] as const,
      lastCount: (storeId: string) => ['stores', 'cash', 'last-count', storeId] as const,
      exceptions: (filters: object) => ['stores', 'cash', 'exceptions', filters] as const,
      position: () => ['stores', 'cash', 'position'] as const,
      holders: () => ['stores', 'cash', 'holders'] as const,
      attachments: (operationId: string) => ['stores', 'cash', 'attachments', operationId] as const,
    },
    shifts: {
      all: ['stores', 'shifts'] as const,
      list: (filters: object) => ['stores', 'shifts', 'list', filters] as const,
      detail: (shiftId: string) => ['stores', 'shifts', 'detail', shiftId] as const,
    },
    registers: {
      all: ['stores', 'registers'] as const,
      list: (kind: string, filters: object) => ['stores', 'registers', kind, filters] as const,
    },
    operations: {
      list: (storeId: string, filters: object) =>
        ['stores', 'operations', 'list', storeId, filters] as const,
      summary: (storeId: string, filters: object) =>
        ['stores', 'operations', 'summary', storeId, filters] as const,
    },
    sales: {
      list: (storeId: string, filters: object) =>
        ['stores', 'sales', 'list', storeId, filters] as const,
      summary: (storeId: string, filters: object) =>
        ['stores', 'sales', 'summary', storeId, filters] as const,
    },
    employees: {
      all: ['stores', 'employees'] as const,
      list: (storeId: string) => ['stores', 'employees', 'list', storeId] as const,
      shifts: (storeId: string, employeeId: string, filters: object) =>
        ['stores', 'employees', 'shifts', storeId, employeeId, filters] as const,
      summary: (storeId: string, filters: object) =>
        ['stores', 'employees', 'summary', storeId, filters] as const,
    },
    inventory: {
      list: (storeId: string, filters: object) =>
        ['stores', 'inventory', 'list', storeId, filters] as const,
      summary: (storeId: string) => ['stores', 'inventory', 'summary', storeId] as const,
      movements: (storeId: string, productId: string) =>
        ['stores', 'inventory', 'movements', storeId, productId] as const,
    },
    products: {
      all: ['stores', 'products'] as const,
      list: (storeId: string, filters: object) =>
        ['stores', 'products', 'list', storeId, filters] as const,
    },
    settings: {
      store: (storeId: string) => ['stores', 'settings', storeId] as const,
      pos: (storeId: string) => ['stores', 'settings', 'pos', storeId] as const,
    },
  },
} as const;

/**
 * Default error handler for React Query.
 *
 * Handles:
 * - Session auth errors: leaves invalidation to the generation-aware API transport
 * - Other errors: logs for debugging
 *
 * Note: This handler is used for both mutations and queries:
 * - Mutation cache onError: (error, variables, context, mutation) → mutation is rest[2]
 * - Query cache onError: (error, query) → query is rest[0]
 */
function handleQueryError(error: unknown, ...rest: unknown[]): void {
  type QueryErrorMeta = {
    skipAuthRedirect?: boolean;
    suppressErrorCodes?: string[];
  };

  // Extract meta from mutation or query
  type MetaHolder = { options?: { meta?: QueryErrorMeta } };
  let meta: QueryErrorMeta | undefined;

  // Try to get meta from query (for query cache errors - rest[0] is the query)
  const query = rest[0] as MetaHolder | undefined;
  if (query?.options?.meta) {
    meta = query.options.meta;
  }

  // Try to get meta from mutation (for mutation cache errors - rest[2] is the mutation)
  const mutation = rest[2] as MetaHolder | undefined;
  if (!meta && mutation?.options?.meta) {
    meta = mutation.options.meta;
  }

  // Allow explicit meta from mutation context (if provided by onMutate)
  const context = rest[1] as { meta?: QueryErrorMeta } | undefined;
  if (!meta && context?.meta) {
    meta = context.meta;
  }

  if (error instanceof ApiError) {
    if (meta?.suppressErrorCodes?.includes(error.error)) {
      return;
    }

    if (error.isStaleAuthSessionResponse()) {
      // Internal cancellation: the caller belongs to a replaced session.
      return;
    }

    if (!error.isSessionAuthError()) {
      console.error('[React Query] Error:', error);
      return;
    }

    // Check if this mutation/query opts to skip auth redirect
    if (meta?.skipAuthRedirect) {
      console.warn('[React Query] Auth error suppressed, skipAuthRedirect enabled');
      return;
    }
    // Session state belongs to the generation-aware transport. React Query
    // must not turn a stale response from an older cookie session into a new
    // logout after a successful login.
    return;
  }

  // Log non-API errors for debugging
  console.error('[React Query] Error:', error);
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= DEFAULT_QUERY_RETRY_COUNT) {
    return false;
  }
  if (!(error instanceof ApiError)) {
    return true;
  }
  if (error.isTenantRuntimeBusy() || error.isSessionAuthError() || error.isClientError()) {
    return false;
  }
  return error.isNetworkError() || error.isServerError();
}

/**
 * Global QueryClient instance with configured defaults.
 *
 * Defaults:
 * - staleTime: 5 minutes for lookups (frequently accessed, rarely changed)
 * - retry: once for transient query errors after API-client retries are exhausted
 * - retry: false for mutations (not idempotent, avoid duplicates)
 * - refetchOnWindowFocus: false (manual refresh for admin panel)
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Set global error handler for queries (using meta approach for React Query v5)
queryClient.getQueryCache().config.onError = handleQueryError;

/**
 * Re-export QueryClientProvider for convenience.
 */
export { TanStackQueryClientProvider as QueryClientProvider };
